import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteCustomerProduct, getCustomerPriceDetail, putCustomerProduct, SOURCE_TONE,
  type CustomerPriceDetail, type CustomerProductBody,
} from "@/services/customerPrices";
import { LoadingState } from "@/shared/ui/PageHeader";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1

/**
 * PRICE-CUST-1 · 客户 × 产品 专属价抽屉（样稿画面④）：只针对这一个客户、这一个产品。
 * 「不设（跟随上一层）」/「设专属价」；设专属价 = 系数 % | 固定金额 $（同行 = 结算价 / 人；多日团按住房档逐档填，某档留空 = 这一档落到下一层）；
 * 生效日期可选（不填 = 一直有效）；低于成本红字，保存弹 REASON-1 原因窗，低于成本时补充说明必填、保存后铃铛通知老板。
 */

const TIERS = ["1", "2", "3", "4"] as const;
const TIER_LABEL: Record<string, string> = { "1": "一人一房", "2": "两人一房", "3": "三人一房", "4": "四人一房" };
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const SIGNED2 = /^-?\d+(\.\d{1,2})?$/;
const usd = (c?: number | null) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const dollars = (c: number | null | undefined) => (c == null ? "" : String(+(c / 100).toFixed(2)));
const toCents = (t: string) => Math.round(Number(t.trim()) * 100);

function moneyError(t: string): string | null {
  const s = t.trim();
  return !s || DECIMAL2.test(s) ? null : "金额最多两位小数";
}

export function CustomerPriceDrawer({ partnerId, productId, onClose, onSaved }: { partnerId: string; productId: string; onClose: () => void; onSaved?: () => void }) {
  const qc = useQueryClient();
  const { ask, dialog: reasonDialog } = useReasonDialog();
  const q = useQuery({ queryKey: ["pricing", "customer-prices", "detail", partnerId, productId], queryFn: () => getCustomerPriceDetail(partnerId, productId) });
  const d: CustomerPriceDetail | undefined = q.data;
  const multi = (d?.occupancies.length ?? 1) > 1;

  const [enabled, setEnabled] = useState(false);          // 设专属价 / 不设
  const [mode, setMode] = useState<"markup" | "fixed">("markup");
  const [pct, setPct] = useState("");
  const [fixed, setFixed] = useState("");
  const [tiers, setTiers] = useState<Record<string, string>>({ "1": "", "2": "", "3": "", "4": "" });
  const [retail, setRetail] = useState("");
  const [retailTiers, setRetailTiers] = useState<Record<string, string>>({ "1": "", "2": "", "3": "", "4": "" });   // PRICE-LIST-TIER-1
  const [child, setChild] = useState("");   // TICKETOPT-1 · 门票儿童结算价（空 = 儿童价落下一层）
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (!d || init) return;
    const o = d.override;
    setEnabled(!!o);
    if (o) {
      setMode(o.mode);
      setPct(o.markup_pct == null ? "" : String(o.markup_pct));
      setFixed(dollars(o.fixed_price_cents));
      setChild(dollars(o.child_fixed_price_cents));
      setTiers(Object.fromEntries(TIERS.map((t) => [t, dollars(o.fixed_tier_prices?.[t])])));
      setRetail(dollars(o.retail_override_cents));
      setRetailTiers(Object.fromEntries(TIERS.map((t) => [t, dollars(o.retail_tier_prices?.[t])])));
      setFrom(o.effective_from ?? ""); setTo(o.effective_to ?? "");
    }
    setInit(true);
  }, [d, init]);

  const costOf = (t: string) => d?.tiers[t]?.cost_cents ?? null;
  const fixedVals = multi ? TIERS.map((t) => tiers[t]) : [fixed];
  const fixedErr = mode === "fixed" ? (fixedVals.map(moneyError).find(Boolean) ?? (fixedVals.some((v) => v.trim()) ? null : "请填写金额")) ?? moneyError(retail) ?? (multi ? TIERS.map((t) => moneyError(retailTiers[t])).find(Boolean) ?? null : null) : null;
  const pctErr = mode === "markup" ? (!pct.trim() ? "请填写系数" : !SIGNED2.test(pct.trim()) ? "最多两位小数" : Number(pct) < -90 || Number(pct) > 500 ? "范围 −90 ~ 500" : null) : null;
  const childOn = (d?.child_cost_cents ?? null) != null;
  const childErr = mode === "fixed" && childOn ? moneyError(child) : null;
  const dateErr = from && to && to < from ? "结束日期不能早于开始日期" : null;
  /** PRICE-LIST-TIER-1 · 某档建议售价低于该档结算价（这一档填了结算价就比它；没填的档后端会按下一层的结算价再核一次） */
  const retailLow = (t: string): string | null => {
    const v = retailTiers[t].trim();
    if (!v || moneyError(v) || !tiers[t].trim() || moneyError(tiers[t])) return null;
    return toCents(v) < toCents(tiers[t]) ? `建议售价不能低于结算价 ${usd(toCents(tiers[t]))}` : null;
  };
  const retailLowAny = mode === "fixed" && multi && TIERS.some((t) => !!retailLow(t));
  const invalid = enabled && (!!fixedErr || !!pctErr || !!dateErr || retailLowAny || !!childErr);

  /** 固定金额低于成本（逐档）——红字；系数模式的低于成本由后端判（负系数把价压到成本以下时保存会要求补充说明） */
  const belowTiers = useMemo(() => {
    if (!d || !enabled || mode !== "fixed") return [] as { tier: string; gap: number }[];
    return (multi ? TIERS : (["2"] as const)).flatMap((t) => {
      const v = (multi ? tiers[t] : fixed).trim();
      const cost = costOf(t) ?? costOf(Object.keys(d.tiers)[0]);
      if (!v || moneyError(v) || cost == null) return [];
      const gap = cost - toCents(v);
      return gap > 0 ? [{ tier: t, gap }] : [];
    });
  }, [d, enabled, mode, multi, tiers, fixed]);   // eslint-disable-line react-hooks/exhaustive-deps

  const body = (): CustomerProductBody => ({
    mode,
    ...(mode === "markup" ? { markup_pct: Number(pct) } : {
      fixed_price_cents: multi ? null : toCents(fixed),
      fixed_tier_prices: multi ? Object.fromEntries(TIERS.filter((t) => tiers[t].trim()).map((t) => [t, toCents(tiers[t])])) : null,
      retail_override_cents: !multi && retail.trim() ? toCents(retail) : null,
      ...(childOn ? { child_fixed_price_cents: child.trim() ? toCents(child) : null } : {}),
      ...(multi ? { retail_tier_prices: Object.fromEntries(TIERS.filter((t) => retailTiers[t].trim()).map((t) => [t, toCents(retailTiers[t])])) } : {}),
    }),
    effective_from: from || null, effective_to: to || null,
  });

  const afterText = mode === "markup" ? `系数 ${Number(pct) >= 0 ? "+" : "−"}${Math.abs(Number(pct))}%` : multi ? "固定金额（按档）" : `固定金额 ${usd(fixed.trim() ? toCents(fixed) : null)}`;
  const beforeText = d?.override ? (d.override.mode === "markup" ? `系数 ${(d.override.markup_pct ?? 0) >= 0 ? "+" : "−"}${Math.abs(d.override.markup_pct ?? 0)}%` : "固定金额") : (d ? `跟随上一层（${d.tiers[Object.keys(d.tiers)[0]]?.fallback_source.label}）` : "—");

  const save = useMutation({
    mutationFn: async () => {
      let needText = belowTiers.length > 0;
      for (let i = 0; i < 2; i += 1) {
        const got = await ask({
          scene: "price_change", title: "设置客户专属价", subtitle: `${d?.partner_name ?? ""} · ${d?.code ?? ""}`,
          changes: [{ label: "专属价", before: beforeText, after: afterText }], confirmText: "确认并保存",
          textRequired: needText, textRequiredHint: "价格低于成本，必须补充说明",
        });
        if (!got) return null;
        try {
          return await putCustomerProduct(partnerId, productId, { ...body(), ...got });
        } catch (e) {
          if (i === 0 && /低于成本/.test((e as Error).message ?? "")) { needText = true; continue; }   // 后端判出低于成本（系数为负等）：再弹一次，补充说明必填
          throw e;
        }
      }
      return null;
    },
    onSuccess: (r) => { if (r) { qc.invalidateQueries({ queryKey: ["pricing"] }); onSaved?.(); onClose(); } },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const got = await ask({ scene: "price_change", title: "恢复跟随", subtitle: `${d?.partner_name ?? ""} · ${d?.code ?? ""}`, required: false, confirmText: "确认恢复", changes: [{ label: "专属价", before: beforeText, after: "跟随上一层" }] });
      if (!got) return false;
      await deleteCustomerProduct(partnerId, productId, got);
      return true;
    },
    onSuccess: (ok) => { if (ok) { qc.invalidateQueries({ queryKey: ["pricing"] }); onSaved?.(); onClose(); } },
  });

  const first = d ? d.tiers[Object.keys(d.tiers)[0]] : null;
  const segCls = (on: boolean) => `inline-flex h-[28px] items-center rounded-md px-3 text-[12px] ${on ? "bg-blue-600 font-semibold text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`;
  const optCls = (on: boolean) => `inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 text-[12.5px] ${on ? "border-blue-600 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white text-slate-600"}`;
  const inp = (bad?: boolean) => `h-7 w-[84px] rounded-[5px] border px-1.5 text-right text-[12.5px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 ${bad ? "border-rose-400" : "border-slate-300"}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/35 backdrop-blur-[2px]" onClick={onClose} data-testid="cp-drawer-bg">
      <aside className="flex h-full w-full max-w-[540px] flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="cp-drawer">
        <div className="flex items-start justify-between gap-2.5 border-b border-slate-200 px-[18px] py-3.5">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">{d ? `${d.partner_name} 专属价` : "客户专属价"}<span className="ml-2 rounded bg-sky-500 px-1.5 text-[10.5px] font-bold text-white">新</span></div>
            <div className="text-[12px] text-slate-500">{d ? `${d.code} ${d.title} · 同行 B2B` : "加载中…"}</div>
          </div>
          <button type="button" aria-label="关闭" onClick={onClose} className="text-[18px] leading-none text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-3.5">
          {q.isLoading || (d && !init) ? <LoadingState /> : q.isError || !d ? (
            <div className="flex flex-col items-center gap-2 py-10 text-[13px] text-slate-500">
              <span>加载失败 · 请重试</span>
              <button onClick={() => q.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
            </div>
          ) : (
            <>
              <div className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
                <span>成本 / 人{multi ? "（一人一房）" : ""}</span>
                <b className="tabular-nums text-slate-900">{usd(multi ? d.tiers["1"]?.cost_cents : first?.cost_cents)}</b>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] text-slate-600" data-testid="cp-fallback">
                <span>不设专属价时：<span className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${SOURCE_TONE[first?.fallback_source.level ?? 1]}`}>{first?.fallback_source.label}</span> →</span>
                <b className="tabular-nums text-slate-900">{usd(multi ? d.tiers["2"]?.fallback_settlement_cents : first?.fallback_settlement_cents)}</b>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <label className={optCls(!enabled)}><input type="radio" className="accent-blue-600" checked={!enabled} onChange={() => setEnabled(false)} data-testid="cp-off" />不设（跟随上一层）</label>
                <label className={optCls(enabled)}><input type="radio" className="accent-blue-600" checked={enabled} onChange={() => setEnabled(true)} data-testid="cp-on" />设专属价</label>
              </div>

              {enabled && (
                <div className="flex flex-col gap-3 rounded-[10px] border border-violet-200 bg-[#FCFBFF] px-3 py-3" data-testid="cp-form">
                  <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                    <span>定价方式</span>
                    <span className="inline-flex overflow-hidden rounded-lg border border-slate-300" role="group" aria-label="定价方式">
                      <button type="button" className={segCls(mode === "markup")} aria-pressed={mode === "markup"} onClick={() => setMode("markup")} data-testid="cp-mode-markup">系数 %</button>
                      <button type="button" className={segCls(mode === "fixed")} aria-pressed={mode === "fixed"} onClick={() => setMode("fixed")} data-testid="cp-mode-fixed">固定金额 $</button>
                    </span>
                  </div>

                  {mode === "markup" ? (
                    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                      <span>在结算价上</span>
                      <span className="flex items-center gap-1.5">
                        <input aria-label="系数" data-testid="cp-pct" value={pct} onChange={(e) => setPct(e.target.value)} inputMode="decimal" className={inp(!!pctErr)} placeholder="+12" />%
                        {pctErr && <span className="text-[11.5px] text-rose-600">{pctErr}</span>}
                        <span className="text-[11.5px] text-slate-400">（负数 = 折扣）</span>
                      </span>
                    </div>
                  ) : multi ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="grid grid-cols-4 gap-1.5">
                        {TIERS.map((t) => (
                          <div key={t} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] text-slate-600">
                            <span>{TIER_LABEL[t]}</span>
                            <span className="flex items-center gap-0.5">$<input aria-label={`${TIER_LABEL[t]}结算价`} data-testid={`cp-tier-${t}`} value={tiers[t]} onChange={(e) => setTiers({ ...tiers, [t]: e.target.value })} inputMode="decimal" className={`${inp(!!moneyError(tiers[t]))} w-[64px]`} /></span>
                            <span className="text-slate-400">成本 {usd(costOf(t))}</span>
                          </div>
                        ))}
                      </div>
                      <span className="text-[11.5px] text-slate-400">每档都要填；某档留空 = 这一档落到下一层。</span>
                      <span className="text-[12.5px] text-slate-600">同行建议售价</span>
                      <div className="grid grid-cols-4 gap-1.5" data-testid="cp-retail-tiers">
                        {TIERS.map((t) => {
                          const low = retailLow(t);
                          return (
                            <div key={t} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] text-slate-600">
                              <span>{TIER_LABEL[t]}</span>
                              <span className="flex items-center gap-0.5">$<input aria-label={`${TIER_LABEL[t]}建议售价`} data-testid={`cp-retail-tier-${t}`} value={retailTiers[t]} onChange={(e) => setRetailTiers({ ...retailTiers, [t]: e.target.value })} inputMode="decimal" className={`${inp(!!moneyError(retailTiers[t]) || !!low)} w-[64px]`} /></span>
                              {low ? <span className="text-rose-600" data-testid={`cp-retail-tier-${t}-err`}>{low}</span> : <span className="text-slate-400">留空 = 结算价 × (1 + 建议加价率)</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                      <span>结算价 / 人</span>
                      <span className="flex items-center gap-1">$<input aria-label="结算价" data-testid="cp-fixed" value={fixed} onChange={(e) => setFixed(e.target.value)} inputMode="decimal" className={inp(!!moneyError(fixed))} placeholder="58.00" /></span>
                    </div>
                  )}
                  {mode === "fixed" && !multi && childOn && (
                    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600" data-testid="cp-child-row">
                      <span>儿童结算价 / 人</span>
                      <span className="flex flex-wrap items-center gap-1.5">$<input aria-label="儿童结算价" data-testid="cp-child" value={child} onChange={(e) => setChild(e.target.value)} inputMode="decimal" className={inp(!!childErr || (!!child.trim() && !childErr && toCents(child) < (d?.child_cost_cents ?? 0)))} placeholder="留空 = 跟随上一层" />
                        <span className="text-[11.5px] text-slate-400">儿童成本 {usd(d?.child_cost_cents)}；上面的「结算价」= 成人</span>
                        {childErr && <span className="text-[11.5px] text-rose-600">{childErr}</span>}</span>
                    </div>
                  )}
                  {mode === "fixed" && !multi && (
                    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                      <span>建议售价</span>
                      <span className="flex items-center gap-1.5 text-[12px] text-slate-500">按加价率；单独填 $<input aria-label="建议售价（单独填）" data-testid="cp-retail" value={retail} onChange={(e) => setRetail(e.target.value)} inputMode="decimal" className={inp(!!moneyError(retail))} /></span>
                    </div>
                  )}
                  {fixedErr && <span className="text-[11.5px] text-rose-600">{fixedErr}</span>}

                  <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                    <span>生效日期</span>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <input type="date" aria-label="开始日期" data-testid="cp-from" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 rounded-[5px] border border-slate-300 px-1.5 text-[12.5px]" />
                      至
                      <input type="date" aria-label="结束日期" data-testid="cp-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 rounded-[5px] border border-slate-300 px-1.5 text-[12.5px]" />
                      <span className="text-[11.5px] text-slate-400">不填 = 一直有效</span>
                      {dateErr && <span className="text-[11.5px] text-rose-600">{dateErr}</span>}
                    </span>
                  </div>

                  {belowTiers.length > 0 && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12.5px] text-rose-700" data-testid="cp-below">
                      ⚠ 低于成本 {usd(Math.max(...belowTiers.map((b) => b.gap)))} / 人。保存需填原因，保存后铃铛通知老板。
                    </div>
                  )}
                </div>
              )}
              {(save.isError || clear.isError) && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="cp-error">保存失败：{((save.error ?? clear.error) as Error)?.message || "请重试"}</div>}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 px-[18px] py-3">
          <button type="button" onClick={() => clear.mutate()} disabled={!d?.override || clear.isPending} data-testid="cp-clear" className="h-[34px] rounded-lg border border-slate-200 px-3.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40">恢复跟随</button>
          <span className="flex gap-2">
            <button type="button" onClick={onClose} className="h-[34px] rounded-lg border border-slate-200 px-3.5 text-[13px] text-slate-700 hover:bg-slate-50">取消</button>
            <button type="button" onClick={() => save.mutate()} disabled={!enabled || invalid || save.isPending} data-testid="cp-save" className="h-[34px] rounded-lg bg-blue-600 px-3.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
              {save.isPending ? "保存中…" : "保存（弹原因）"}
            </button>
          </span>
        </div>
        {reasonDialog}
      </aside>
    </div>
  );
}
